/**
 * Copyright 2026 Ziz Academy
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types';

const Register: React.FC = () => {
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<UserRole>('STUDENT');
    const [error, setError] = useState('');
    const { register, isLoading, user } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.role === 'ADMIN';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        try {
            const normalizedEmail = email.trim().toLowerCase();
            if (normalizedEmail !== email) {
                setEmail(normalizedEmail);
            }
            await register({ username: normalizedEmail, password, name, email: normalizedEmail, role, id: 0 } as any);
            navigate('/');
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Registration failed. Please try again.'
            setError(errorMessage);
        }
    };

    return (
        <div className="auth-page" data-testid="register-page">
            <div className="auth-card">
                <span className="auth-emoji">🌈</span>
                <h1 className="auth-title">Join the Fun!</h1>
                <p className="auth-subtitle">Create your account and start learning! 🌟</p>

                {error && <div className="alert alert-error" role="alert">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label className="form-label" htmlFor="name">
                            😊 Your name
                        </label>
                        <input
                            type="text"
                            id="name"
                            className="form-input"
                            placeholder="What should we call you?"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            data-testid="name-input"
                        />
                    </div>


                    <div className="form-group">
                        <label className="form-label" htmlFor="email">
                            📧 Email
                        </label>
                        <input
                            type="email"
                            id="email"
                            className="form-input"
                            placeholder="Your email address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onBlur={(e) => setEmail(e.target.value.trim().toLowerCase())}
                            title="Please enter a valid email like name@example.com"
                            autoComplete="email"
                            inputMode="email"
                            required
                            data-testid="email-input"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label" htmlFor="password">
                            🔒 Password
                        </label>
                        <input
                            type="password"
                            id="password"
                            className="form-input"
                            placeholder="New password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                            data-testid="password-input"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label" htmlFor="role">
                            🎭 I am a...
                        </label>
                        <select
                            id="role"
                            className="form-select"
                            value={role}
                            onChange={(e) => setRole(e.target.value as UserRole)}
                            data-testid="role-select"
                        >
                            <option value="STUDENT">🎒 Student</option>
                            <option value="TEACHER">👩‍🏫 Teacher</option>
                        </select>
                    </div>

                    <button
                        type="submit"
                        className="btn btn-secondary btn-lg"
                        style={{ width: '100%' }}
                        disabled={isLoading}
                        data-testid="register-button"
                    >
                        {isLoading ? '⌛ Creating...' : '🎉 Create Account!'}
                    </button>
                </form>

                <p style={{ textAlign: 'center', marginTop: '1.5rem', color: '#718096' }}>
                    Already have an account?{' '}
                    <Link to="/login" style={{ color: '#667eea', fontWeight: 'bold' }}>
                        Login here! 🚀
                    </Link>
                </p>
            </div>
        </div>
    );
};

export default Register;